import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { onError } from '../../utils/error.util';
import { createContractingItem, getContractingUploadUrl } from '../../services/contracting.service';
import { ContractingFormSchema, contractingFormInitialValues } from './validationSchema';
import { CompanyConfig } from '../../company.config';
import { useNoIndex } from '../../hooks/noindex.hook';
import './contracting-page.css';

type FileField = 'billOfSale' | 'titleFront' | 'titleBack';

const UPLOAD_LABELS: Record<FileField, string> = {
  billOfSale: 'Bill of Sale / Purchase Agreement',
  titleFront: 'MCO / MSO / Title - Front',
  titleBack: 'MCO / MSO / Title - Back',
};

const CDN_STYLESHEETS = [
  'https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css',
  'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css',
];

export default function ContractingPage() {
  useNoIndex();
  const { opportunityId = '', dealerName = '', analystEmail = '' } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [files, setFiles] = useState<Record<FileField, File | null>>({
    billOfSale: null,
    titleFront: null,
    titleBack: null,
  });
  const [fileErrors, setFileErrors] = useState<Partial<Record<FileField, string>>>({});

  useEffect(() => {
    document.body.classList.add('ccl-dash');
    const links = CDN_STYLESHEETS.map((href) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
      return link;
    });
    return () => {
      document.body.classList.remove('ccl-dash');
      links.forEach((link) => link.remove());
    };
  }, []);

  function handleFileSelect(field: FileField, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setFiles((prev) => ({ ...prev, [field]: file }));
    if (file) setFileErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  const onSubmit = async (values: typeof contractingFormInitialValues) => {
    if (honeypot) return;

    const missing: Partial<Record<FileField, string>> = {};
    (Object.keys(UPLOAD_LABELS) as FileField[]).forEach((field) => {
      if (!files[field]) missing[field] = 'Please attach this document';
    });
    if (Object.keys(missing).length > 0) {
      setFileErrors(missing);
      return;
    }

    setIsSubmitting(true);
    try {
      const uploadOne = async (field: FileField) => {
        const file = files[field]!;
        const { uploadUrl, fileKey } = await getContractingUploadUrl(file.name, opportunityId || undefined);
        const putRes = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });
        if (!putRes.ok) throw new Error(`Upload failed for ${file.name}`);
        return fileKey;
      };

      const [billOfSaleFileKey, titleFrontFileKey, titleBackFileKey] = await Promise.all([
        uploadOne('billOfSale'),
        uploadOne('titleFront'),
        uploadOne('titleBack'),
      ]);

      await createContractingItem({
        dealerName,
        opportunityId,
        analystEmail,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        salesTaxCollected: values.salesTaxCollected as 'yes' | 'no',
        registrationFeesCollected: values.registrationFeesCollected as 'yes' | 'no',
        titleTransferCompleted: values.titleTransferCompleted as 'yes' | 'no',
        registrationTransferCompleted: values.registrationTransferCompleted as 'yes' | 'no',
        billOfSaleFileKey,
        titleFrontFileKey,
        titleBackFileKey,
        dealNotes: values.dealNotes,
        disclosureAgreementName: values.disclosureAgreementName,
      });
      setSubmitted(true);
    } catch (err) {
      onError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <>
        <section className="hero-section">
          <div className="container hero-content">
            <span className="hero-eyebrow">{CompanyConfig.name}</span>
            <h1>Contracting</h1>
          </div>
        </section>
        <section className="dealer-form-section py-5 text-center">
          <div className="container">
            <h2 className="white">Thank You!</h2>
            <p className="lead-text-light mt-3">
              Your Contracting form has been submitted. {CompanyConfig.shortName} has been emailed a summary and will be in touch shortly.
            </p>
          </div>
        </section>
        <footer className="ccl-footer">
          <a href={CompanyConfig.website} target="_blank" rel="noopener" title={CompanyConfig.name}>
            <img src={CompanyConfig.logoUrl} alt={CompanyConfig.name} />
          </a>
          <p className="ccl-footer-url">
            <a href={CompanyConfig.website} target="_blank" rel="noopener">
              www.collectorcarleaders.com
            </a>
          </p>
        </footer>
      </>
    );
  }

  return (
    <>
      <div className="ccl-contracting-header" role="banner">
        <section className="container">
          <div className="row">
            <div className="col-7 col-sm-5 col-md-5 col-lg-3 col-xl-2 py-2">
              <a href={CompanyConfig.website} target="_blank" rel="noopener" title={CompanyConfig.name}>
                <img alt={CompanyConfig.name} className="img-fluid" src={CompanyConfig.logoUrl} />
              </a>
            </div>
          </div>
        </section>
      </div>

      <main id="main">
        {/* Hero */}
        <section className="hero-section">
          <div className="container hero-content">
            <span className="hero-eyebrow">{CompanyConfig.name}</span>
            <h1>Contracting</h1>
          </div>
        </section>

        {/* Overview */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="row">
              <div className="col-12 col-lg-10 mx-auto">
                <div className="intro-plain">
                  <h4>Before You Begin</h4>
                  <p>
                    Answer the questions below and upload the required documents. Have digital copies of the Bill of
                    Sale / Purchase Agreement and the MCO / MSO / Title (front and back) ready to attach.
                  </p>
                  <p>If any answer changes after you submit, contact {CompanyConfig.shortName} directly so we can update the deal.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Formik
          initialValues={contractingFormInitialValues}
          validationSchema={ContractingFormSchema}
          onSubmit={onSubmit}
        >
          {({ handleChange, values, touched, errors }) => (
            <Form noValidate>
              <input
                type="text"
                name="reject"
                className="field-vanish"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />

              <section className="bg-dk-blue white py-5 px-4 dealer-form-section" id="contracting-form">
                <div className="container">
                  <div className="row">
                    <div className="col-12 col-lg-11 mx-auto">
                      <div className="sale-form-card">
                        {/* Contact Information */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Contact Information</h4>

                          <div className="row">
                            <div className="col-12 col-md-4">
                              <label htmlFor="firstName">First Name</label>
                              <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                className="form-control"
                                value={values.firstName}
                                onChange={handleChange}
                              />
                              {touched.firstName && errors.firstName && (
                                <span className="field-error">{errors.firstName}</span>
                              )}
                            </div>
                            <div className="col-12 col-md-4">
                              <label htmlFor="lastName">Last Name</label>
                              <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                className="form-control"
                                value={values.lastName}
                                onChange={handleChange}
                              />
                              {touched.lastName && errors.lastName && (
                                <span className="field-error">{errors.lastName}</span>
                              )}
                            </div>
                            <div className="col-12 col-md-4">
                              <label htmlFor="email">Email</label>
                              <input
                                type="email"
                                id="email"
                                name="email"
                                className="form-control"
                                value={values.email}
                                onChange={handleChange}
                              />
                              {touched.email && errors.email && (
                                <span className="field-error">{errors.email}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <hr className="form-section-divider" />

                        {/* Contracting Questions */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Questions</h4>

                          <div className="row">
                            <div className="col-12">
                              <p className="form-p">
                                <span className="form-p-num">1.</span>Are you collecting Sales Tax?
                              </p>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="salesTaxCollected"
                                  value="yes"
                                  checked={values.salesTaxCollected === 'yes'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">Yes</span>
                              </label>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="salesTaxCollected"
                                  value="no"
                                  checked={values.salesTaxCollected === 'no'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">No</span>
                              </label>
                              {touched.salesTaxCollected && errors.salesTaxCollected && (
                                <span className="field-error">{errors.salesTaxCollected}</span>
                              )}
                            </div>
                          </div>

                          <hr className="form-section-divider" />

                          <div className="row">
                            <div className="col-12">
                              <p className="form-p">
                                <span className="form-p-num">2.</span>Are you collecting Registration fees?
                              </p>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="registrationFeesCollected"
                                  value="yes"
                                  checked={values.registrationFeesCollected === 'yes'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">Yes</span>
                              </label>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="registrationFeesCollected"
                                  value="no"
                                  checked={values.registrationFeesCollected === 'no'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">No</span>
                              </label>
                              {touched.registrationFeesCollected && errors.registrationFeesCollected && (
                                <span className="field-error">{errors.registrationFeesCollected}</span>
                              )}
                            </div>
                          </div>

                          <hr className="form-section-divider" />

                          <div className="row">
                            <div className="col-12">
                              <p className="form-p">
                                <span className="form-p-num">3.</span>Are you completing the Title transfer?
                              </p>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="titleTransferCompleted"
                                  value="yes"
                                  checked={values.titleTransferCompleted === 'yes'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">Yes</span>
                              </label>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="titleTransferCompleted"
                                  value="no"
                                  checked={values.titleTransferCompleted === 'no'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">No</span>
                              </label>
                              {touched.titleTransferCompleted && errors.titleTransferCompleted && (
                                <span className="field-error">{errors.titleTransferCompleted}</span>
                              )}
                            </div>
                          </div>

                          <hr className="form-section-divider" />

                          <div className="row">
                            <div className="col-12">
                              <p className="form-p">
                                <span className="form-p-num">4.</span>Are you completing the Registration transfer?
                              </p>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="registrationTransferCompleted"
                                  value="yes"
                                  checked={values.registrationTransferCompleted === 'yes'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">Yes</span>
                              </label>
                              <label className="form-check form-check-inline radio-pill">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="registrationTransferCompleted"
                                  value="no"
                                  checked={values.registrationTransferCompleted === 'no'}
                                  onChange={handleChange}
                                />
                                <span className="form-check-label">No</span>
                              </label>
                              {touched.registrationTransferCompleted && errors.registrationTransferCompleted && (
                                <span className="field-error">{errors.registrationTransferCompleted}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <hr className="form-section-divider" />

                        {/* Required Uploads */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Required Uploads</h4>
                          <p className="form-section-intro">
                            All three documents are required. Each must be the <strong>full document</strong>,{' '}
                            <strong>clear and legible</strong>.
                            <br />
                            <strong>File types:</strong> PDF, JPG, or PNG. Max 5 MB per file.
                          </p>

                          {(Object.keys(UPLOAD_LABELS) as FileField[]).map((field, i) => (
                            <div key={field}>
                              <div className="row">
                                <div className="col-12">
                                  <p className="upload-label">
                                    <span className="form-p-num">{i + 1}.</span>
                                    {UPLOAD_LABELS[field]}
                                  </p>
                                  <div className="file-attach-row">
                                    <label className={`file-drop${files[field] ? ' has-file' : ''}`}>
                                      <span className="file-drop-icon">
                                        <i className={`fas ${files[field] ? 'fa-check' : 'fa-paperclip'}`}></i>
                                      </span>
                                      <span className="fd-primary">{files[field]?.name ?? 'Attach file'}</span>
                                      <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) => handleFileSelect(field, e)}
                                      />
                                    </label>
                                  </div>
                                  {fileErrors[field] && <span className="field-error">{fileErrors[field]}</span>}
                                </div>
                              </div>
                              {i < 2 && <hr className="form-section-divider" />}
                            </div>
                          ))}
                        </div>

                        <hr className="form-section-divider" />

                        {/* Deal Notes */}
                        <div className="form-section">
                          <h4 className="form-section-heading">Deal Notes</h4>
                          <div className="row">
                            <div className="col-12">
                              <label htmlFor="dealNotes">
                                Anything the {CompanyConfig.shortName} team should know about this deal?{' '}
                                <small style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>
                                  (optional)
                                </small>
                              </label>
                              <textarea
                                id="dealNotes"
                                name="dealNotes"
                                className="form-control deal-notes-textarea"
                                rows={5}
                                placeholder="Delivery timing, buyer preferences, paperwork nuances, anything else worth flagging..."
                                value={values.dealNotes}
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Standalone Disclosure (conditional) */}
                        {(values.titleTransferCompleted === 'no' || values.registrationTransferCompleted === 'no') && (
                          <div className="disclosure-block" style={{ display: 'block' }}>
                            <h5>Standalone Disclosure</h5>
                            <p className="disclosure-body">
                              If you are not completing the title OR registration transfer, by submitting the MCO /
                              Title and Bill of Sale, you are promising to mail the MCO / Title to {CompanyConfig.shortName}, with a
                              tracking number, within 10 days of receiving funds.
                            </p>

                            <div className="disclosure-sig-row">
                              <div>
                                <label htmlFor="disclosureAgreementName">Type your full name to agree</label>
                                <input
                                  type="text"
                                  id="disclosureAgreementName"
                                  name="disclosureAgreementName"
                                  className="form-control"
                                  autoComplete="off"
                                  value={values.disclosureAgreementName}
                                  onChange={handleChange}
                                />
                                {touched.disclosureAgreementName && errors.disclosureAgreementName && (
                                  <span className="field-error">{errors.disclosureAgreementName}</span>
                                )}
                              </div>
                              <div>
                                <label>Date</label>
                                <div className="disclosure-sig-date">
                                  {new Date().toLocaleDateString('en-US', {
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: 'numeric',
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="text-center mt-5">
                          <button id="btnSubmit" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                          </button>
                          <span className="submit-note d-block">{CompanyConfig.shortName} is emailed a summary the moment you submit.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </Form>
          )}
        </Formik>
      </main>

      <footer className="ccl-footer">
        <a href={CompanyConfig.website} target="_blank" rel="noopener" title={CompanyConfig.name}>
          <img src={CompanyConfig.logoUrl} alt={CompanyConfig.name} />
        </a>
        <p className="ccl-footer-url">
          <a href={CompanyConfig.website} target="_blank" rel="noopener">
            www.collectorcarleaders.com
          </a>
        </p>
      </footer>
    </>
  );
}
